<template>
  <modal
    class="cancellation-modal"
    @close="closeCancellationModal"
    :allowClose="submitted && !submissionError"
  >
    <template #header>Let us know the reason for cancelling your application?</template>
    <div class="body">
      <form class="form cancellation" v-if="!submitted && !submissionError">
        <fieldset>
          <div class="xfields">
            <base-form-field
              :schema="cancellationFormSchema.cancellationReasonId"
              :options="cancellationReasonOptions"
              v-model.number="info.cancellationReasonId"
              :disabled="submittingRequest || rtcPeriodEndedError"
              @change="handleReasonChange(info)"
            >
              <template #default-option>Select the reason</template>
            </base-form-field>
            <base-form-field
              class="hide comment-area"
              v-model="info.comment"
              :schema="cancellationFormSchema.comment"
            />
          </div>
        </fieldset>
        <div class="form buttons">
          <template v-if="applicationInRTCPeriod || applicationRTCPeriodFound">
            <p class="warning">
              Are you sure you want to cancel this application? This action cannot be undone.
            </p>
            <div class="buttons-wrapper">
              <next-button text="Yes" @click.prevent="submitForm" :loading="submittingRequest" />
              <next-button text="No" @click.prevent="closeCancellationModal" />
            </div>
          </template>
          <template v-else>
            <next-button
              text="Submit"
              @click.prevent="submitForm"
              :loading="submittingRequest"
              :disabled="submittingRequest || rtcPeriodEndedError"
            />
          </template>
        </div>
      </form>
      <div v-if="submitted && !submissionError">
        {{ confirmationText }}
      </div>
      <div v-if="submitted && submissionError"><error /></div>
      <div v-if="rtcPeriodEndedError" class="warning">
        {{ rtcPeriodEndedErrorMessage }}. Please email our support team at
        <a :href="`mailto:${emailAddress}`"> {{ emailAddress }}</a> with any questions or concerns.”
      </div>
    </div>
    <template #footer></template>
  </modal>
</template>
<script setup lang="ts">
import { reactive, computed, ref, onMounted } from "vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import type { FieldSchema, FormSchema } from "@/models/form";
import type { LOSType } from "@/models/opal";
import Modal from "@/components/shared/Modal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import { $api } from "@/services/api";
import Error from "@/components/shared/Error.vue";
import useValidation from "@/use/validation";
import { ApplicationStatusTypeIds } from "@/config/constants";
import { ProductIds } from "@/config/constants";
import useApplicationData from "@/composables/useApplicationData";

const emit = defineEmits(["closeCancellationForm", "cancelApplication"]);
const store = useStore();
const route = useRoute();
interface Props {
  application: any;
}

const props = withDefaults(defineProps<Props>(), {});
const { emailAddress } = useApplicationData();

const cancellationReasons = computed(
  () => store.state.lookupsModule.lookups["applicationStatusReasons"],
);
const submittingRequest = ref<boolean>(false);
const submitted = ref<boolean>(false);
const submissionError = ref<boolean>(false);
const rtcPeriodEndedError = ref<boolean>(false);
const rtcPeriodEndedErrorMessage = ref<string>("");
const { validateForm } = useValidation();

let info = reactive({
  cancellationReasonId: undefined,
  cancellationReason: undefined,
} as any);

const confirmationText = ref<string>(
  "Your cancellation request has been submitted. You will be notified via email once the request is completed. It usually takes 2 to 3 business days.",
);
const cancellationReasonOptions = computed(() =>
  (cancellationReasons.value ?? []).map((sr: LOSType) => {
    return {
      label: sr.description,
      value: sr.id,
    };
  }),
);

const productTypeId = computed(() => store.state.appData?.application?.productTypeId || props.application.productTypeId);
const applicationStatusId = computed(() => store.state.appData?.application?.applicationStatusId || props.application.applicationStatusId);
const referenceId = computed<string>(
  () =>
    (route?.params?.referenceId as string) ||
    store.getters["modal/cancellationReferenceId"] ||
    "",
);
const applicationInRTCPeriod = computed(
  () => applicationStatusId.value === ApplicationStatusTypeIds.RIGHT_TO_CANCEL,
);
const applicationRTCPeriodFound = ref<boolean>(false);

const checkRTCPeriodOnMounted = computed<boolean>(
  () =>
    (!applicationInRTCPeriod.value && productTypeId.value === ProductIds.REFI) ||
    (!applicationInRTCPeriod.value && productTypeId.value === ProductIds.PEL),
);

const cancellationFormSchema = reactive({
  cancellationReasonId: {
    name: "cancellationReasonId",
    type: "select",
    label: "Cancellation Reason",
    errorMessage: "A cancellation reason is required",
    hideLabel: true,
    required: true,
  } as FieldSchema,
  comment: {
    name: "comment",
    type: "textarea",
    hideLabel: true,
    required: false,
  } as FieldSchema,
}) as FormSchema;

function handleReasonChange(evt: any) {
  const statusReasonName =
    cancellationReasons.value.find((cr: any) => cr.id === evt.cancellationReasonId)?.name || null;
  info.cancellationReason = statusReasonName;
}
function resetForm() {
  info.cancellationReasonId = undefined;
  info.cancellationReason = undefined;
}
function closeCancellationModal() {
  if (submitted.value && !submissionError.value) {
    emit("cancelApplication");
  }
  store.dispatch("modal/closeCancellationModal");
  resetForm();
}

async function submitForm() {
  submissionError.value = false;
  submittingRequest.value = true;

  const valid = validateForm(cancellationFormSchema, {
    cancellationReasonId: info.cancellationReasonId,
  });
  if (valid) {
    
    const response = await $api.applications.cancelApplication(
      referenceId.value,
      props.application.lenderId,
      info,
    );
    
    
    if (
      response?.results?.id &&
      response?.results?.statusReasonId &&
      response?.results?.statusReasonName
    ) {
      store.dispatch("setApplication", response.results);
      submitted.value = true;
      submittingRequest.value = false;
    }

    if (response.errors && response?.errors?.length > 0) {
      submittingRequest.value = false;

      // Handle RTC Error
      if (response.errors[0]?.includes("Right to Cancel period ending")) {
        rtcPeriodEndedError.value = true;
        rtcPeriodEndedErrorMessage.value = response.errors[0];
      }
      // Handle all other errors
      else {
        submittingRequest.value = false;
        submissionError.value = true;
      }
    }
  } else {
    submittingRequest.value = false;
  }
}

async function checkRTCPeriod() {
  applicationRTCPeriodFound.value = false;
  rtcPeriodEndedError.value = false;
  rtcPeriodEndedErrorMessage.value = "";

  const response = await $api.applications.checkRTCPeriod(referenceId.value);

  // RTC available app is in RTC period
  if (response.results === "RightToCancelAvailable") {
    applicationRTCPeriodFound.value = true;
  }

  // no RTC found - allow cancellation app is NOT in RTC period
  if (response.results === "RightToCancelNotFound") {
    applicationRTCPeriodFound.value = false;
  }

  // RTC period ended
  if (response.results === "RightToCancelEnded") {
    rtcPeriodEndedError.value = true;
    rtcPeriodEndedErrorMessage.value = response.errors[0];
  }
}

onMounted(() => {
  if (checkRTCPeriodOnMounted.value) {
    checkRTCPeriod();
  }
});
</script>
<style scoped>
.cancellation-modal .body {
  padding: 1.563rem 1.5rem;
  pointer-events: auto;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
  width: 100%;
}

.cancellation-modal .body .form.buttons {
  /* text-align: right; */
  padding: 1.563rem 0;
}

.buttons-wrapper {
  display: flex;
}
.buttons-wrapper button {
  min-width: 60px;
}
.buttons-wrapper button:first-of-type {
  margin-right: 10px;
}

.warning {
  color: red;
  font-weight: 700;
}
</style>
